import axios from "axios";
import { API_URL } from "@/lib/constants";
import { API_ENDPOINTS } from "@/lib/api-constants";

// Create a separate axios instance for token refresh that doesn't include auth interceptors
const refreshAxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

interface RefreshTokenResponse {
  response_schema: {
    response_code: string;
    response_message: string;
  };
  response_output: {
    detail: {
      access_token: string;
      refresh_token?: string;
      token_type: string;
      expires_in: number;
    };
  };
}

class TokenRefreshService {
  private static instance: TokenRefreshService;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string | null) => void> = [];

  static getInstance(): TokenRefreshService {
    if (!TokenRefreshService.instance) {
      TokenRefreshService.instance = new TokenRefreshService();
    }
    return TokenRefreshService.instance;
  }

  async refreshAccessToken(refreshToken: string): Promise<string> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.refreshSubscribers.push((token: string | null) => {
          if (token) {
            resolve(token);
          } else {
            reject(new Error('Token refresh failed'));
          }
        });
      });
    }

    this.isRefreshing = true;

    try {
      const response = await refreshAxiosInstance.post<RefreshTokenResponse>(
        API_ENDPOINTS.AUTH.REFRESH,
        { refresh_token: refreshToken }
      );

      const newAccessToken = response.data.response_output.detail.access_token;
      
      this.refreshSubscribers.forEach(callback => callback(newAccessToken));
      this.refreshSubscribers = [];

      this.isRefreshing = false;
      return newAccessToken;
    } catch (error) {
      this.refreshSubscribers.forEach(callback => callback(null));
      this.refreshSubscribers = [];
      this.isRefreshing = false;
      
      throw error;
    }
  }

  onRefreshFailed() {
    this.refreshSubscribers.forEach(callback => callback(null));
    this.refreshSubscribers = [];
  }
}

export const tokenRefreshService = TokenRefreshService.getInstance();
